// Copyright © SRGTech (Software Resonance Group Inc.). All rights reserved.
// SPDX-License-Identifier: LicenseRef-SRGTech-Proprietary
// See LICENSE and docs/legal/PROPRIETARY_COPYRIGHT.md.

/**
 * Runtime validators for BFF responses.
 *
 * `docs/05-CODING-STANDARDS.md` §9 requires external input to be parsed with
 * Zod; a network response is external input even when it comes from our own
 * BFF. These schemas are the trust boundary: nothing reaches a React component
 * without passing through one.
 *
 * The schemas are **not** hand-copied models. Each one is bound to its
 * generated contract type by a compile-time assertion in
 * {@link ./contract-compatibility}, so a contract change in root `shared/`
 * breaks `tsc` here instead of silently drifting
 * (`ui/docs/Implementation.md` §5, `TST-010` generated-code drift checks).
 */

import { z } from 'zod';

/** Non-sensitive string attribute bag shared by several contracts. */
const attributeBag = z.record(z.string(), z.string()).default({});

/** Non-sensitive classification tags. */
const tagList = z.array(z.string()).default([]);

/** Optional ISO-8601 timestamp that the contract models as nullable. */
const nullableTimestamp = z.string().nullable().default(null);

/** Optional bounded string that the contract models as nullable. */
const nullableText = z.string().nullable().default(null);

/** `PrincipalType` enumeration. */
export const principalTypeSchema = z.enum(['user', 'service', 'system', 'agent', 'other']);

/** `AuditOutcome` enumeration. */
export const auditOutcomeSchema = z.enum(['success', 'failure', 'denied', 'unknown']);

/** `ConnectorStatus` enumeration. */
export const connectorStatusSchema = z.enum(['active', 'inactive', 'disabled', 'error', 'unknown']);

/** `ConnectorHealthStatus` enumeration. */
export const connectorHealthStatusSchema = z.enum(['healthy', 'degraded', 'unhealthy', 'unknown']);

/** `PolicyEffect` enumeration. */
export const policyEffectSchema = z.enum(['allow', 'deny', 'not_applicable', 'indeterminate']);

/** `MemoryKind` enumeration. */
export const memoryKindSchema = z.enum(['fact', 'preference', 'summary', 'episode', 'other']);

/** `DataClassificationLevel` enumeration. */
export const dataClassificationLevelSchema = z.enum([
  'public',
  'internal',
  'confidential',
  'restricted',
  'highly_restricted',
]);

/** `Pagination` validator. */
export const paginationSchema = z.object({
  page: z.number().int(),
  page_size: z.number().int(),
  total_items: z.number().int(),
  total_pages: z.number().int(),
  has_next: z.boolean(),
  has_previous: z.boolean(),
});

/** `College` validator. */
export const collegeSchema = z.object({
  college_id: z.string(),
  university_id: z.string(),
  code: z.string(),
  name: z.string(),
  is_active: z.boolean().default(true),
});

/**
 * Lifecycle state of a registered college, exactly as Platform emits it.
 *
 * The three values are `TenantStatus` in
 * `platform/.../tenant_provisioning.py`. Not a frontend enum: inventing one
 * would let the UI display a state the control plane never produces, and an
 * unknown value must fail validation rather than be rendered.
 */
export const tenantStatusSchema = z.enum(['pending', 'active', 'deactivated']);

/** Lifecycle state of a registered college. */
export type TenantStatus = z.infer<typeof tenantStatusSchema>;

/**
 * A registered college as the control plane holds it (**ADR-0067**).
 *
 * Mirrors Platform's `TenantRecord` field for field. It is deliberately *not*
 * {@link collegeSchema}: that shape belongs to the generated `College`
 * contract — a different resource with `college_id`, `university_id`, and
 * `is_active` — and the University Control Center's tenant registry is served
 * by `TenantRecord`, which has none of those. Deriving `is_active` from a
 * record that carries `status` was displaying a state the server never sent.
 *
 * `address` defaults to empty because Platform defaults it. The two token
 * fields are present because the contract carries them and dropping a field
 * from a validator makes the schema silently lossy; nothing renders them —
 * `token_tail` identifies a credential and has no place on a registry screen.
 */
export const registeredTenantSchema = z.object({
  id: z.string(),
  name: z.string(),
  code: z.string(),
  address: z.string().default(''),
  status: tenantStatusSchema,
  registered_at: z.string(),
  token_issued_at: z.string().nullable().default(null),
  token_tail: z.string().nullable().default(null),
});

/** `University` validator. */
export const universitySchema = z.object({
  university_id: z.string(),
  code: z.string(),
  name: z.string(),
  is_active: z.boolean().default(true),
});

/** `Permission` validator. */
export const permissionSchema = z.object({
  code: z.string(),
  name: nullableText,
  resource: nullableText,
  action: nullableText,
});

/** `Role` validator. */
export const roleSchema = z.object({
  code: z.string(),
  name: z.string(),
  description: nullableText,
  permissions: z.array(permissionSchema).default([]),
});

/** `Locale` validator. */
export const localeSchema = z.object({
  language: z.string(),
  locale_code: z.string(),
  region: nullableText,
});

/** `UserProfile` validator. */
export const userProfileSchema = z.object({
  user_id: z.string(),
  display_name: nullableText,
  email: nullableText,
  preferred_locale: localeSchema.nullable().default(null),
  attributes: attributeBag,
});

/** `AuditActor` validator. */
export const auditActorSchema = z.object({
  actor_id: z.string(),
  actor_type: principalTypeSchema,
  display_name: nullableText,
});

/** `KnowledgeDocument` validator. */
export const knowledgeDocumentSchema = z.object({
  document_id: z.string(),
  title: z.string(),
  summary: nullableText,
  source: nullableText,
  uri: nullableText,
  content_type: nullableText,
  created_at: nullableTimestamp,
  updated_at: nullableTimestamp,
  tags: tagList,
  attributes: attributeBag,
});

/** `ConnectorCapability` validator. */
export const connectorCapabilitySchema = z.object({
  code: z.string(),
  name: z.string(),
  description: nullableText,
});

/** `ConnectorMetadata` validator. */
export const connectorMetadataSchema = z.object({
  connector_id: z.string(),
  name: z.string(),
  provider: nullableText,
  version: z.string().default('1.0.0'),
  status: connectorStatusSchema.default('unknown'),
  capabilities: z.array(connectorCapabilitySchema).default([]),
  tags: tagList,
  attributes: attributeBag,
});

/** `ConnectorHealth` validator. */
export const connectorHealthSchema = z.object({
  status: connectorHealthStatusSchema,
  latency_ms: z.number().int().nullable().default(null),
  message: nullableText,
  checked_at: nullableTimestamp,
});

/** `Obligation` validator. */
export const obligationSchema = z.object({
  obligation_id: z.string(),
  code: z.string(),
  description: nullableText,
  attributes: attributeBag,
});

/** `Restriction` validator. */
export const restrictionSchema = z.object({
  restriction_id: z.string(),
  code: z.string(),
  description: nullableText,
  attributes: attributeBag,
});

/** `PolicyDecision` validator. */
export const policyDecisionSchema = z.object({
  decision_id: z.string(),
  effect: policyEffectSchema,
  policy_id: nullableText,
  reason: nullableText,
  obligations: z.array(obligationSchema).default([]),
  restrictions: z.array(restrictionSchema).default([]),
  decided_at: nullableTimestamp,
});

/** `MemoryMetadata` validator. */
export const memoryMetadataSchema = z.object({
  kind: memoryKindSchema.default('other'),
  source: nullableText,
  tags: tagList,
  created_at: nullableTimestamp,
  expires_at: nullableTimestamp,
  attributes: attributeBag,
});

/** `MemoryRecord` validator. */
export const memoryRecordSchema = z.object({
  memory_id: z.string(),
  content: z.string(),
  kind: memoryKindSchema.default('other'),
  subject_id: nullableText,
  tenant_id: nullableText,
  session_id: nullableText,
  conversation_id: nullableText,
  metadata: memoryMetadataSchema.nullable().default(null),
  created_at: nullableTimestamp,
  updated_at: nullableTimestamp,
  expires_at: nullableTimestamp,
  attributes: attributeBag,
});

/** `Metrics` validator. */
export const metricsSchema = z.object({
  name: z.string(),
  value: z.number(),
  unit: nullableText,
  recorded_at: nullableTimestamp,
  labels: attributeBag,
});

/** `AgentCapability` validator. */
export const agentCapabilitySchema = z.object({
  code: z.string(),
  name: z.string(),
  description: nullableText,
});

/** `AgentMetadata` validator. */
export const agentMetadataSchema = z.object({
  agent_id: z.string(),
  name: z.string(),
  version: z.string().default('1.0.0'),
  provider: nullableText,
  capabilities: z.array(agentCapabilitySchema).default([]),
  tags: tagList,
  attributes: attributeBag,
});

/** `MCPServer` validator. */
export const mcpServerSchema = z.object({
  server_id: z.string(),
  name: z.string(),
  description: nullableText,
  endpoint_uri: nullableText,
  version: z.string().default('1.0.0'),
  capabilities: z.array(agentCapabilitySchema).default([]),
  tags: tagList,
});

/** `Citation` validator. */
export const citationSchema = z.object({
  citation_id: z.string(),
  source_title: z.string(),
  source_uri: nullableText,
  document_id: nullableText,
  page_number: z.number().int().nullable().default(null),
  snippet: nullableText,
});

/** `DataClassification` validator. */
export const dataClassificationSchema = z.object({
  classification_id: z.string(),
  level: dataClassificationLevelSchema,
  description: nullableText,
  labels: tagList,
});

/**
 * Build the validator for a bounded collection response.
 *
 * Every list endpoint returns the same envelope so pagination controls,
 * empty states, and bounds are implemented once (`API-001`).
 *
 * @param item - Validator for a single item.
 * @returns Validator for `{ items, pagination }`.
 */
export function listResponseSchema<TItem extends z.ZodType>(
  item: TItem,
): z.ZodObject<{ items: z.ZodDefault<z.ZodArray<TItem>>; pagination: typeof paginationSchema }> {
  return z.object({
    items: z.array(item).default([]),
    pagination: paginationSchema,
  });
}

/** A bounded collection of `TItem`. */
export interface ListResponse<TItem> {
  readonly items: readonly TItem[];
  readonly pagination: z.infer<typeof paginationSchema>;
}

/**
 * Validated shapes returned by the clients.
 *
 * These are the *parsed* types, not the raw contract types: a validator fills
 * every documented default, so `tags` and `attributes` are always present
 * downstream. {@link ./contract-compatibility} proves each one still satisfies
 * its generated contract, so consuming these adds precision without forking a
 * contract.
 */
export type ValidatedCollege = z.infer<typeof collegeSchema>;

/**
 * Validated `TenantRecord` — the University Control Center's tenant registry.
 *
 * Distinct from {@link ValidatedCollege}, which remains bound to the generated
 * `College` contract and its own consumers.
 */
export type RegisteredTenant = z.infer<typeof registeredTenantSchema>;

/** Validated `University`. */
export type ValidatedUniversity = z.infer<typeof universitySchema>;

/** Validated `Role`. */
export type ValidatedRole = z.infer<typeof roleSchema>;

/** Validated `Permission`. */
export type ValidatedPermission = z.infer<typeof permissionSchema>;

/** Validated `UserProfile`. */
export type ValidatedUserProfile = z.infer<typeof userProfileSchema>;

/** Validated `KnowledgeDocument`. */
export type ValidatedKnowledgeDocument = z.infer<typeof knowledgeDocumentSchema>;

/** Validated `ConnectorMetadata`. */
export type ValidatedConnectorMetadata = z.infer<typeof connectorMetadataSchema>;

/** Validated `ConnectorHealth`. */
export type ValidatedConnectorHealth = z.infer<typeof connectorHealthSchema>;

/** Validated `MemoryRecord`. */
export type ValidatedMemoryRecord = z.infer<typeof memoryRecordSchema>;

/** Validated `Metrics`. */
export type ValidatedMetrics = z.infer<typeof metricsSchema>;

/** Validated `AgentMetadata`. */
export type ValidatedAgentMetadata = z.infer<typeof agentMetadataSchema>;

/** Validated `MCPServer`. */
export type ValidatedMcpServer = z.infer<typeof mcpServerSchema>;

/** Validated `Citation`. */
export type ValidatedCitation = z.infer<typeof citationSchema>;

/** Validated `AuditActor`. */
export type ValidatedAuditActor = z.infer<typeof auditActorSchema>;

/** Validated `DataClassification`. */
export type ValidatedDataClassification = z.infer<typeof dataClassificationSchema>;
